import { useState, useMemo } from "react";
import {
  Box,
  Typography,
  ListItem,
  ListItemText,
  Button,
  Tooltip,
} from "@common-ui/ui";
import {
  Edit,
  DeleteForever,
  LocalShipping,
  Apartment,
  LocalPostOffice,
  BusinessCenter,
} from "@mui/icons-material";
import MapIcon from "@mui/icons-material/Map";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";

import AddressDialog from "../dialogs/AddressDialog";
import AddressRemovalDialog from "../dialogs/AddressRemovalDialog";

const addressIcons = {
  Shipping: <LocalShipping />,
  Billing: <BusinessCenter />,
  Postal: <LocalPostOffice />,
  Building: <Apartment />,
};

const Addresses = ({
  orgId,
  isSuccess,
  setIsSuccess,
  customerId,
  addresses,
  doesUserHavePermission,
}) => {
  const [addressRows, setAddressRows] = useState([]);
  const [mode, setMode] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [removeAddressDialog, setRemoveAddressDialog] = useState(false);
  const [addressId, setAddressId] = useState("");

  useMemo(() => {
    if (addresses && addresses.length) {
      setAddressRows(addresses.map((a) => ({ ...a, id: a.system_id })));
    } else {
      setAddressRows([]);
    }
  }, [addresses]);

  const addressColumns = [
    {
      field: "type",
      renderHeader: () => <Typography variant="subtitle1">Type</Typography>,
      width: 165,
      renderCell: (params) => (
        <Tooltip title={params.value} placement="left" arrow>
          <Box>{addressIcons[params.value] ?? <MapIcon />}</Box>
        </Tooltip>
      ),
    },
    {
      field: "city",
      renderHeader: () => <Typography variant="subtitle1">City</Typography>,
      width: 165,
    },
    {
      field: "state_or_province",
      renderHeader: () => (
        <Typography variant="subtitle1">State/Province</Typography>
      ),
      width: 165,
    },
    {
      field: "country_or_region",
      renderHeader: () => <Typography variant="subtitle1">Country</Typography>,
      width: 165,
    },
    {
      field: "id",
      renderHeader: () => <Typography variant="subtitle1">Actions</Typography>,
      width: 230,
      renderCell: (params) => {
        return (
          <Box>
            {doesUserHavePermission ? (
              <>
                <Button
                  startIcon={<Edit />}
                  onClick={() => {
                    setMode("edit");
                    setOpenDialog(true);
                    setAddressId(params.id);
                  }}
                  sx={{ mr: 4 }}
                >
                  Edit
                </Button>
                <Button
                  startIcon={<DeleteForever />}
                  onClick={() => {
                    setAddressId(params.id);
                    setRemoveAddressDialog(true);
                  }}
                >
                  Remove
                </Button>
              </>
            ) : (
              <></>
            )}
          </Box>
        );
      },
    },
  ];

  const handleCreateNewAddress = () => {
    setMode("add");
    setOpenDialog(true);
  };

  return (
    <>
      <ListItem
        alignItems="flex-start"
        sx={{ pr: 0, pl: 0, pt: 8, justifyContent: "flex-start" }}
      >
        <ListItemText
          primary={
            <>
              <Typography color="textPrimary" variant="h4">
                Addresses
              </Typography>
            </>
          }
        />
        <div>
          {doesUserHavePermission ? (
            <Button
              color="primary"
              fullWidth
              size="medium"
              type="button"
              variant="contained"
              onClick={handleCreateNewAddress}
            >
              Add New
            </Button>
          ) : (
            ""
          )}
        </div>
      </ListItem>
      <Box
        component="main"
        sx={{
          alignItems: "center",
          display: "flex",
          flexGrow: 1,
          minHeight: "100%",
          backgroundColor: "#FFFFFF",
          ".MuiDataGrid-virtualScroller": {
            marginTop: "40px !important",
          },
          ".MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
          },
          maxWidth: "100%",
        }}
      >
        <DataGridPro
          loading={false}
          rows={addressRows}
          columns={addressColumns}
          pagination
          components={{ Toolbar: GridToolbar }}
          disableSelectionOnClick
          autoHeight={true}
          sx={{
            border: 0,
            ".MuiDataGrid-columnSeparator": {
              display: "none",
            },

            "& .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: "#F8F8F8",
              border: "1px solid #EBEBEB",
              minHeight: "40px !important",
              maxHeight: "40px !important",
              lineHeight: "40px !important",
            },
            ".MuiDataGrid-virtualScroller": {
              marginTop: "40px",
            },
          }}
        />
      </Box>
      <AddressDialog
        open={openDialog}
        mode={mode}
        edit={false}
        orgId={orgId}
        isSuccess={isSuccess}
        setIsSuccess={setIsSuccess}
        customerId={customerId}
        addresses={addresses}
        addressId={addressId}
        handleClose={() => {
          setAddressId("");
          setOpenDialog(false);
        }}
      />
      <AddressRemovalDialog
        open={removeAddressDialog}
        orgId={orgId}
        setIsSuccess={setIsSuccess}
        customerId={customerId}
        addressId={addressId}
        handleClose={() => {
          setAddressId("");
          setRemoveAddressDialog(false);
        }}
      />
    </>
  );
};

export default Addresses;
